import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
	SetDigitalTwinsPageNumber,
	SetDigitalTwinsTotal,
} from "../../Actions/DigitalTwinsList";
import {
	batteryPackListActions,
	batteryPackSearchSuggestions,
	getBatteryPackList,
} from "../../Api/BatteryPackApi";
import { SetSessionExpired } from "../../Actions";
import CODE from "../../Static/Constants/StatusCodes";
import { fetchFiltersDataObject } from "../../Helper/Table/TableHelper";
import AppCache from "../../Helper/Common/AppCache";
import GenericDraggableTableView from "../../Features/Table/GenericDraggableTable";
import { useTranslation } from "react-i18next";
import { MAX_COLUMN_SELECTION_COUNT } from "../../Helper/DigitalTwin/DataFile";
import { getAvailableEmailDomains } from "../../Helper/Organization/EmailDomains";
import {
	buildBatteryPackRequestFilters,
	isImpossibleSearchFilterCombination,
} from "../../Helper/Inventory/batteryPackListHelper";

const COLUMN_SETTING_STORAGE_KEY = "DIGITAL_TWIN_COLUMN_SETTINGS";
// Tab order: 0 All Vehicles, 1 Battery Configurations, 2 All Battery Packs,
// 3 Unmapped IOT Devices.
//
// Deliberately placed AFTER Battery Configurations rather than second. All Vehicles and
// Battery Configurations share filterConfigurations.digitalTwinListTable and distinguish
// themselves by these numeric indices, so inserting at position 1 would have renumbered
// Configurations and silently dropped every filter whose `tabs` array said [0, 1] — in
// every org's config. Landing here keeps 0 and 1 stable, so this tab is fully independent:
// it reads its own filterConfigurations.batteryPackListTable and no existing config moves.
const TAB_INDEX = 2;
const TABLE_META_KEY = "batteryPackListTable";

// Fallback for orgs whose pageMeta does not yet carry
// table.batteryPackListTable.searchableFields. Keys must match the backend's
// SEARCHABLE_COLUMNS (pack_master_db_helper.py).
const DEFAULT_SEARCHABLE_FIELDS = [
	{ key: "customerPackID", title: "Battery Serial Number" },
	{ key: "deviceID", title: "IMEI" },
	{ key: "vin", title: "Chassis Number" },
	{ key: "licensePlateNo", title: "Bus Number" },
];

export default function BatteryPackListTab({
	selectedFilters,
	setSelectedFilters,
	sorting,
	setSorting,
	searchTerm,
	setSearchTerm,
	visibleColumnKeys,
	setVisibleColumnKeys,
	pagesContent,
	columns: allColumns,
	totalBatteryPacks,
}) {
	const { t } = useTranslation();
	const dispatch = useDispatch();
	const pageNumber = useSelector((state) => state.DigitalTwinsPageNumber.value);
	const rowsPerPage = useSelector((state) => state.DigitalTwinsRowsPerPage.value);
	const total = useSelector((state) => state.DigitalTwinsTotal.value);

	const [isLoading, setIsLoading] = useState(true);
	const [columns, setColumns] = useState([]);
	const [filters, setFilters] = useState({});
	const [selectedColumns, setSelectedColumns] = useState([]);
	const [columnOrder, setColumnOrder] = useState([]);
	const [defaultSelectedColumns, setDefaultSelectedColumns] = useState([]);
	const [totalSelectedRowKeys, setTotalSelectedRowKeys] = useState(new Set());
	const [isSelectAll, setIsSelectAll] = useState(false);

	const [list, setList] = useState({
		packs: [],
		pages: { start: 1, end: 0, total: 0 },
		responseStatus: { code: CODE.LOADING, msg: "" },
	});

	const lastFetchParams = useRef(null);
	const columnsInitialised = useRef(false);
	const disableDownloads = pagesContent?.disableDownloads === true;
	// Memoised so the `?? []` fallback does not hand a fresh array to setupTabColumns'
	// dependency list on every render.
	const tabColumns = useMemo(
		() => allColumns?.[TAB_INDEX]?.columns ?? [],
		[allColumns]
	);

	const searchableFields = useMemo(() => {
		const fromMeta = pagesContent?.table?.[TABLE_META_KEY]?.searchableFields;
		return fromMeta?.length ? fromMeta : DEFAULT_SEARCHABLE_FIELDS;
	}, [pagesContent]);

	const getCurrentSorting = () => sorting?.[TAB_INDEX] || {};

	// One-shot, ref-guarded on purpose. Guarding on `columns.length > 0` with `columns` in
	// the dependency list is an infinite loop whenever tabColumns is empty (pageMeta has no
	// batteryPackListTable yet): setColumns([]) hands back a fresh array, `columns` changes
	// identity, the callback is recreated, the effect refires, the length guard is still
	// false — and each pass also calls setVisibleColumnKeys, re-rendering the parent.
	const setupTabColumns = useCallback(() => {
		if (columnsInitialised.current) return;
		// Nothing to derive from yet. Do NOT mark as initialised — pageMeta may still
		// arrive, and this must run once it does.
		if (!tabColumns.length) return;

		columnsInitialised.current = true;

		const defaultColumns = [...tabColumns]
			.sort((a, b) => {
				const weight = (col) => (col.fixed === "left" ? 0 : col.fixed === "right" ? 2 : 1);
				return weight(a) - weight(b);
			})
			.slice(0, MAX_COLUMN_SELECTION_COUNT);

		const columnSettingKeys = AppCache.getItem(COLUMN_SETTING_STORAGE_KEY) || {};
		const columnSettingTabKeys = columnSettingKeys[TAB_INDEX] || [];

		const filteredColumns = columnSettingTabKeys.length
			? tabColumns.filter(({ dataIndex }) => columnSettingTabKeys.includes(dataIndex))
			: defaultColumns;

		const filteredColumnKeys = columnSettingTabKeys.length
			? columnSettingTabKeys
			: defaultColumns.map(({ dataIndex }) => dataIndex);

		setDefaultSelectedColumns(defaultColumns.map(({ dataIndex }) => dataIndex));
		setColumns(filteredColumns);
		setSelectedColumns(filteredColumnKeys);
		setVisibleColumnKeys((prev) => ({
			...prev,
			[TAB_INDEX]: filteredColumnKeys,
		}));
	}, [tabColumns, setVisibleColumnKeys]);

	const setupFilters = async () => {
		const tabFilters = pagesContent?.filterConfigurations?.[TABLE_META_KEY]?.filters;
		if (!tabFilters) {
			return;
		}

		const filtersObj = await fetchFiltersDataObject(tabFilters, filters);
		const sortedFiltersObj = Object.fromEntries(
			Object.entries(filtersObj).sort(([, a], [, b]) => a.label.localeCompare(b.label))
		);
		setFilters(sortedFiltersObj);
	};

	const applySort = (key, order) => {
		const tempSort = [];
		tempSort[TAB_INDEX] = { sortBy: key, orderBy: order };
		setSorting(tempSort);
	};

	const onPageChange = (page) => {
		dispatch(SetDigitalTwinsPageNumber(page));
	};

	// Search values are folded into `filters` keyed by the selected search column,
	// matching the other inventory tabs. Selecting IMEI / Chassis Number / Bus Number
	// implicitly narrows the list to packs mapped to a battery — the backend's LEFT JOIN
	// plus WHERE predicate handles that, no client-side special-casing needed.
	const buildRequestFilters = () =>
		buildBatteryPackRequestFilters(searchTerm, selectedFilters);

	// Selection is keyed on record.id, NOT customerPackID: GenericDraggableTable renders
	// rowKey={(record) => record.id || record.key}, and antd matches selectedRowKeys against
	// that key. Keying on the serial made the count correct while no checkbox ever rendered
	// as checked, because no selectedRowKey matched a rowKey.
	const onSelectChange = (record, selected) => {
		const updated = new Set(totalSelectedRowKeys);
		if (selected) {
			updated.add(record.id);
		} else {
			updated.delete(record.id);
			if (isSelectAll) setIsSelectAll(false);
		}
		setTotalSelectedRowKeys(updated);
	};

	const onSelectAllChange = (selected, selectedRows) => {
		setIsSelectAll(selected);
		setTotalSelectedRowKeys(
			selected
				? new Set(selectedRows.map((row) => row?.id).filter(Boolean))
				: new Set()
		);
	};

	// Always report the real selection, including under select-all. GenericDraggableTable
	// reuses this as its "is anything selected?" test for the clear-selection warning
	// (confirmSelectionClear), so returning [] for select-all — as the sibling tabs do —
	// silently suppresses that warning in exactly the case where the most work is lost.
	// The export is unaffected: `selectAll: true` makes the backend ignore resourceIDs.
	const getTotalRowSelection = () => Array.from(totalSelectedRowKeys);

	const getTotalRowSelectionCount = () =>
		isSelectAll ? list?.pages?.total : totalSelectedRowKeys.size;

	const getCurrentPageSelection = () => {
		const currentPageIDs = (list.packs || []).map((pack) => pack.id).filter(Boolean);
		if (isSelectAll) return currentPageIDs;
		return Array.from(totalSelectedRowKeys).filter((id) => currentPageIDs.includes(id));
	};

	const handleClearSelection = () => {
		setTotalSelectedRowKeys(new Set());
		setIsSelectAll(false);
	};

	const getCSVFileName = () =>
		`AllBatteryPacks_${new Date().toLocaleDateString("en-US", {
			year: "numeric",
			month: "long",
			day: "numeric",
		})}.csv`;

	// Export sends the same filters and sort as the list, so the CSV matches the table.
	const buildActionPayload = (actionType, extra = {}) => ({
		actionType,
		resourceIDs: getTotalRowSelection(),
		selectAll: isSelectAll,
		filters: buildRequestFilters(),
		sortBy: sorting?.[TAB_INDEX]?.sortBy || "customerPackID",
		order: sorting?.[TAB_INDEX]?.orderBy || "asc",
		columnOrder,
		columns: JSON.stringify(columns),
		...extra,
	});

	const getCSVData = async () => {
		const res = await batteryPackListActions(buildActionPayload("download-csv"));
		if (res && res.responseStatus.code === 200) {
			return res.response.data;
		}
		if (res && res.responseStatus.code === -2) dispatch(SetSessionExpired(true));
		return [];
	};

	const onSendEmail = async ({ sendTo, sendCc, subject, body }) => {
		const res = await batteryPackListActions(
			buildActionPayload("mail", {
				emailTo: sendTo,
				emailCc: sendCc,
				subject,
				body,
			})
		);
		if (res && res.responseStatus.code === 200) {
			return res.response.data;
		}
		if (res && res.responseStatus.code === -2) dispatch(SetSessionExpired(true));
		return [];
	};

	const applySelectedColumns = (newSelectedColumns) => {
		const filteredColumns = tabColumns.filter((columnConfig) =>
			newSelectedColumns.includes(columnConfig.dataIndex)
		);

		setColumns(filteredColumns);
		setSelectedColumns(newSelectedColumns);

		const updatedSelectedColumns = { ...(AppCache.getItem(COLUMN_SETTING_STORAGE_KEY) || {}) };
		updatedSelectedColumns[TAB_INDEX] = newSelectedColumns;
		AppCache.setItem(COLUMN_SETTING_STORAGE_KEY, updatedSelectedColumns);

		setVisibleColumnKeys((prev) => ({
			...prev,
			[TAB_INDEX]: newSelectedColumns,
		}));

		setColumnOrder(filteredColumns.map((col) => col.headerLabel || col.title));
	};

	const onColumnOrderChange = (tableColumns) => {
		setColumnOrder(tableColumns.map((value) => value.headerLabel));
	};

	const shouldShowFilteredCount = () => {
		if (isLoading) return false;
		return (
			searchTerm?.selectedDevices?.length > 0 ||
			Object.keys(selectedFilters || {}).length > 0
		);
	};

	async function GetListOfBatteryPacks() {
		setIsLoading(true);

		const res = await getBatteryPackList({
			filters: buildRequestFilters(),
			pageNumber,
			rowsPerPage,
			sortBy: sorting?.[TAB_INDEX]?.sortBy,
			orderBy: sorting?.[TAB_INDEX]?.orderBy,
		});

		if (res?.responseStatus?.code === 200) {
			setList({
				packs: res.response.data || [],
				pages: res.response.total,
				responseStatus: res.responseStatus,
			});
			dispatch(SetDigitalTwinsTotal(res.response.total?.total));
		} else {
			if (res?.responseStatus?.code === -2) dispatch(SetSessionExpired(true));
			setList({
				packs: [],
				pages: res?.response?.total,
				responseStatus: {
					code: res?.responseStatus?.code,
					msg: res?.responseStatus?.msg,
				},
			});
		}

		setIsLoading(false);
	}

	useEffect(() => {
		setupTabColumns();
	}, [tabColumns, setupTabColumns]);

	// Deliberately NOT gated on visibleColumnKeys, unlike the sibling tabs. There the gate
	// matters because the request body carries the visible columns (it drives which
	// extended attributes the backend joins). /api/battery-packs takes no columns at all —
	// only paging, sort and filters — so gating on them just meant the list never loaded
	// when pageMeta had no batteryPackListTable, showing "No matches found" next to a tab
	// count of 1.
	useEffect(() => {
		const currentParams = {
			pageNumber,
			selectedFilters: JSON.stringify(selectedFilters),
			searchTerm: JSON.stringify(searchTerm),
			sorting: JSON.stringify(sorting[TAB_INDEX]),
		};

		if (JSON.stringify(currentParams) !== JSON.stringify(lastFetchParams.current)) {
			lastFetchParams.current = currentParams;
			GetListOfBatteryPacks();
		}
	}, [pageNumber, selectedFilters, searchTerm, sorting]);

	useEffect(() => {
		if (pageNumber !== 1) {
			dispatch(SetDigitalTwinsPageNumber(1));
		}
	}, [selectedFilters, searchTerm, sorting[TAB_INDEX]]);

	useEffect(() => {
		if (list.responseStatus.code !== CODE.LOADING) {
			setupFilters();
		}
	}, [list.responseStatus.code]);

	const tableData = list.responseStatus.code === CODE.SUCCESS ? list.packs : [];

	return (
		<GenericDraggableTableView
			tableID="BatteryPackList"
			title={t("inventory:tabs.battery_packs.label", "All Battery Packs")}
			count={totalBatteryPacks}
			primaryButtonLabel={null}
			showPrimaryButton={false}
			onPrimaryButtonClick={() => {}}
			applySearch={(selectedDevices, selectedSearchColumn) =>
				setSearchTerm({ selectedDevices, selectedSearchColumn })
			}
			searchableFields={searchableFields}
			disableSearch={false}
			searchSuggestionsHandler={batteryPackSearchSuggestions}
			disableDownloads={disableDownloads}
			getTotalRowSelection={getTotalRowSelection}
			getTotalRowSelectionCount={getTotalRowSelectionCount}
			getCSVData={getCSVData}
			getCSVFileName={getCSVFileName}
			onSendEmail={onSendEmail}
			availableEmailDomains={getAvailableEmailDomains(pagesContent)}
			filters={filters}
			selectedFilters={selectedFilters}
			applyFilters={(args) => setSelectedFilters(args)}
			initialColumns={tabColumns}
			selectedColumns={selectedColumns}
			applySelectedColumns={applySelectedColumns}
			filteredCount={shouldShowFilteredCount() ? list?.pages?.total : 0}
			tableColumns={columns}
			tableData={tableData}
			onColumnOrderChange={onColumnOrderChange}
			columnOrder={columnOrder}
			isLoading={isLoading}
			pageNumber={pageNumber}
			rowsPerPage={rowsPerPage}
			total={total}
			applySort={applySort}
			onPageChange={onPageChange}
			onClearSelection={handleClearSelection}
			emptyMessage={
				// Distinguish "no data" from "no columns configured". Without this, a
				// pageMeta missing batteryPackListTable looks identical to an empty
				// registry — which is what made this hard to diagnose.
				!tabColumns.length
					? "Battery pack columns are not configured for this organisation (pageMeta table.batteryPackListTable)."
					: isImpossibleSearchFilterCombination(searchTerm, selectedFilters)
					? "Searching by IMEI, Chassis Number or Bus Number only matches packs mapped to a battery. Remove the Unmapped filter to see results."
					: "No matches found for filters applied"
			}
			customRightActions={false}
			initialSorting={getCurrentSorting()}
			maxColumnsAllowed={MAX_COLUMN_SELECTION_COUNT}
			defaultSelectedColumns={defaultSelectedColumns}
			rowSelection={{
				selectedRowKeys: getCurrentPageSelection(),
				onSelect: onSelectChange,
				onSelectAll: onSelectAllChange,
			}}
		/>
	);
}
